"use client"

import { PrimaryButton, SecondaryButton } from "@/components/buttons"
import { ButtonSurface } from "@/components/buttons/ButtonSurface"
import { useEffect, useState } from "react"
import Cookies from "js-cookie"

const COOKIE_NAME = "agnos_cookie_consent"

interface ConsentState {
  hasGrantedConsent: boolean
  hasConsentDecision: boolean
}

export function MdxConsentManager() {
  const [consentState, setConsentState] = useState<ConsentState>({
    hasGrantedConsent: false,
    hasConsentDecision: false,
  })
  const [loading, setLoading] = useState(true)

  // Detect if we're in a secure context (HTTPS or localhost)
  const isSecureContext =
    typeof window !== "undefined" &&
    (window.location.protocol === "https:" ||
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1")

  useEffect(() => {
    const consentCookie = Cookies.get(COOKIE_NAME)
    if (consentCookie) {
      setConsentState({
        hasGrantedConsent: consentCookie === "true",
        hasConsentDecision: true,
      })
    }
    setLoading(false)
  }, [])

  const handleGrantConsent = () => {
    Cookies.set(COOKIE_NAME, "true", {
      expires: 180,
      path: "/",
      sameSite: "lax",
      secure: isSecureContext && window.location.protocol === "https:",
    })
    setConsentState({
      hasGrantedConsent: true,
      hasConsentDecision: true,
    })
    // Update Google Analytics consent
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("consent", "update", {
        ad_storage: "granted",
        ad_user_data: "granted",
        ad_personalization: "granted",
        analytics_storage: "granted",
      })
    }
    // Reload to apply consent changes
    window.location.reload()
  }

  const handleRevokeConsent = () => {
    Cookies.set(COOKIE_NAME, "false", {
      expires: 30,
      path: "/",
      sameSite: "lax",
      secure: isSecureContext && window.location.protocol === "https:",
    })
    setConsentState({
      hasGrantedConsent: false,
      hasConsentDecision: true,
    })
    // Update Google Analytics consent
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("consent", "update", {
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
        analytics_storage: "denied",
      })
    }
    // Reload to apply consent changes
    window.location.reload()
  }

  if (loading) {
    return (
      <div className="rounded-lg border border-edge-200 bg-surface-100 p-6">
        <p className="text-content-secondary">Loading consent status...</p>
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-edge-200 bg-surface-100 p-6">
      <h3 className="mb-4 text-xl font-semibold text-content-primary">
        Your Current Consent Status
      </h3>

      <div className="mb-6">
        {consentState.hasConsentDecision ? (
          <div className="flex items-baseline gap-2">
            <div
              className={`h-3 w-3 rounded-full ${
                consentState.hasGrantedConsent ? "bg-green-500" : "bg-red-500"
              }`}
            />
            <p className="text-content-secondary">
              Analytics cookies are currently{" "}
              <strong className="text-content-primary">
                {consentState.hasGrantedConsent ? "enabled" : "disabled"}
              </strong>
            </p>
          </div>
        ) : (
          <div className="flex items-baseline gap-2">
            <div className="h-3 w-3 rounded-full bg-yellow-500" />
            <p className="text-content-secondary">
              You have not yet made a consent decision
            </p>
          </div>
        )}
      </div>

      <div className="space-y-4">
        <div className="rounded-md bg-surface-200 p-4">
          <h4 className="mb-2 font-medium text-content-primary">
            Analytics Cookies
          </h4>
          <p className="mb-3 text-sm text-content-secondary">
            We use Google Analytics to understand how visitors interact with our
            website. This helps us improve our content and user experience.
          </p>
          <p className="text-sm text-content-tertiary">
            Data collected includes: page views, time spent on pages, browser
            type, and general location (country/city level).
          </p>
        </div>

        <div className="flex gap-3">
          {consentState.hasGrantedConsent ? (
            <SecondaryButton
              buttonProps={{ onClick: handleRevokeConsent }}
              className="bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
              surface={ButtonSurface.OnNeutral}
            >
              Revoke Consent
            </SecondaryButton>
          ) : (
            <PrimaryButton
              buttonProps={{ onClick: handleGrantConsent }}
              className="bg-green-600 text-white hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600"
              surface={ButtonSurface.OnNeutral}
            >
              Grant Consent
            </PrimaryButton>
          )}
        </div>
      </div>
    </div>
  )
}
